import React, { Fragment } from 'react';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';
import { HTML_SPECIAL_CHAR_REGEX } from './constants';

/**
 * Escapes the special characters of a regular expression
 * @param string A regexp string to escape
 * @returns scaped regexp
 */
export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
}; // $& means the whole matched string

/**
 * Replaces tokens in a string
 * @param string - A string with sequential tokens to be replaced. ex: "Hello {0}! It's me {1}!"
 * @param replacements - Strings that replace a token {index-1} (the first node in replacements replaces {0}, 2nd -> {1})
 * @param encodeReplacement - Option to decode the replacements as URIComponent
 */
export const replaceTokens = (
  string: string,
  replacements: string[],
  encodeReplacement: boolean
): string => {
  return (replacements || []).reduce(
    (accumulator, currentValue, index) =>
      accumulator?.replace(
        new RegExp(escapeRegExp(`{${index}}`), 'g'),
        encodeReplacement ? encodeURIComponent(currentValue) : currentValue
      ),
    string
  );
};

/**
 * Replaces tokens in a string
 * @param  string - A string with sequential tokens to be replaced. ex: "Hello {0}! It's me {1}!"
 * @param  replacements - Strings that replace a token {index-1} (the first node in replacements replaces {0}, 2nd -> {1})
 * @returns - Returns a string with tokens (ex. {0}, {1}) replaced with it's corresponding replacement.
 * @example
 * 		// returns 'Hello world! It's me Keith!'
 * 		replaceTokensInString("Hello {0}! It's me {1}!", 'world', 'Keith');
 * @license - Credit https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
 * 		(Mozilla code samples added on or after August 20, 2010 are in the public domain (CC0). No licensing notice is necessary)
 */
export const replaceTokensInString = (
  string: string,
  ...replacements: string[]
): string | undefined => {
  return !string ? undefined : replaceTokens(string, replacements, false);
};

/**
 * Replaces tokens in a string and return the string URL-encoded
 * @param url - A url string with sequential tokens to be replaced. ex: "/wcs/resources/store/{0}/whatever/{1}"
 * @param replacements - Strings that replace a token {index-1} (the first node in replacements replaces {0}, 2nd -> {1})
 * @returns - Returns a string with tokens (ex. {0}, {1}) replaced with it's corresponding URI-encoded replacement.
 * @example
 * 		// returns '/wcs/resources/store/test/whatever/%25stuff'
 * 		replaceTokensInUrl("/wcs/resources/store/{0}/whatever/{1}", 'test', '%stuff');
 * @license - Credit https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
 * 		(Mozilla code samples added on or after August 20, 2010 are in the public domain (CC0). No licensing notice is necessary)
 */
export const replaceTokensInUrl = (
  string: string,
  ...replacements
): string | undefined =>
  !string ? undefined : replaceTokens(string, replacements, true);

/**
 * Replaces tokens with component
 * @param string - A string with sequential tokens to be replaced. ex: "Hello {0}! It's me {1}!"
 * @param replacements - components that replace a token {index-1} (the first node in replacements replaces {0}, 2nd -> {1})
 * @returns
 * We are using index as key as these elements are static
 */
export const replaceTokensWithComponents = (
  string: string,
  ...replacements: React.ReactNode[]
): React.ReactNode[] => {
  const regEx = /(\{\d+\})/g;
  const substring: React.ReactNode[] = string?.split(regEx);
  replacements.forEach((replacement, index) => {
    const replaceTextIndex = substring?.findIndex(
      (part) => part === `{${index}}`
    );
    substring[replaceTextIndex] = (
      <Fragment key={uuidv4()}>{replacement}</Fragment>
    );
  });
  return substring;
};

/**
 * removes spaces from String
 * @param str
 * @returns The initially entered string with the spaces removed
 */
export const removeSpacesFromString = (str: string): string => {
  if (typeof str !== 'string') {
    return '';
  }
  return str.replace(/\s+/g, '');
};

/**
 * capitalize a string.
 * @param text
 * @param shouldTransformToLowerCase
 * @returns
 */
export const capitalizeText = (
  text: string,
  shouldTransformToLowerCase = true
) => {
  return text
    .split(' ')
    .map((word) => {
      const str = shouldTransformToLowerCase
        ? word.slice(1).toLowerCase()
        : word.slice(1);
      return word.charAt(0).toUpperCase() + str;
    })
    .join(' ');
};

export const getProductName = (text: string) => {
  return text
    .split('-')
    .map((word) => {
      const str = capitalizeText(word.toLowerCase());
      return str;
    })
    .join('-');
};

/**
 * decode a string.
 * @param term
 * @returns
 */
export const decodeString = (term: string) => {
  return decodeURIComponent((term + '').replace(/\+/g, '%20'));
};

/**
 * Use this function to decode html entities in a HTML string like &reg; or &amp;
 * @param {string} htmlString HTML with entities
 * @returns{string} string with decoded entities like &,>,<,®,~
 */
export function decodeHTML(htmlString: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = htmlString;
  return DOMPurify.sanitize(txt.value);
}

/**
 * format a currency.
 * @param value
 * @param currency
 * @param locale
 * @returns
 */
export const getFormatedCurrency = (
  value: number,
  currency: string,
  locale: string
) => {
  return value.toLocaleString(locale, {
    style: 'currency',
    currency: currency
  });
};

export const hasHtmlInjectionTags = (
  currentValue,
  errorMsg,
  errorPattern = HTML_SPECIAL_CHAR_REGEX
) => {
  if (currentValue.match(errorPattern)) {
    return errorMsg;
  }
  return true;
};
